/**
 * Step MCP Transcript
 *
 * Renders the persisted MCP tool-call transcript from step metadata
 * (`metadataJson.mcpTranscript`, written by the mcp node executor — bounded to
 * the most recent 25 entries, secret-masked and truncated server-side). Shows
 * one compact expandable chip per tool call: name, duration, error badge, and
 * on expand the recorded args + result text.
 *
 * @module FlowDesigner/runs/StepMcpTranscript
 */
import React, { useState } from 'react';
import { CaretDown, CaretRight, CheckCircle, PlugsConnected, XCircle } from '@phosphor-icons/react';
import { Text } from '@chatsdet/ui';
import { CopyablePre } from '../../../components/CopyButton';

export interface McpTranscriptEntry {
  toolName: string;
  /** JSON-ish args string (already secret-masked + truncated server-side). */
  args: string;
  /** Result text (already secret-masked + truncated server-side). */
  result: string;
  isError: boolean;
  durationMs: number;
}

/** Narrow unknown step metadata to the persisted transcript entries. */
export function parseMcpTranscript(value: unknown): McpTranscriptEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is McpTranscriptEntry =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as { toolName?: unknown }).toolName === 'string'
  );
}

export function StepMcpTranscript({ entries }: { entries: McpTranscriptEntry[] }) {
  if (entries.length === 0) return null;

  return (
    <div className="ml-4 mt-1 space-y-1">
      {entries.map((entry, i) => (
        <McpTranscriptRow key={`${i}-${entry.toolName}`} entry={entry} />
      ))}
    </div>
  );
}

function McpTranscriptRow({ entry }: { entry: McpTranscriptEntry }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium transition-colors cursor-pointer bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/30"
      >
        {entry.isError ? (
          <XCircle size={12} weight="bold" className="text-red-600 dark:text-red-400" />
        ) : (
          <CheckCircle size={12} weight="bold" className="text-green-600 dark:text-green-400" />
        )}
        <PlugsConnected size={12} weight="bold" />
        <span className="truncate max-w-[220px] font-mono">{entry.toolName}</span>
        <span className="text-sky-500 dark:text-sky-400 text-[10px]">{entry.durationMs}ms</span>
        {expanded ? <CaretDown size={10} /> : <CaretRight size={10} />}
      </button>

      {expanded && (
        <div className="ml-4 mt-1 space-y-1">
          {entry.args && entry.args !== '{}' && (
            <CopyablePre value={entry.args} label="args">
              <pre className="text-[10px] leading-tight bg-gray-50 dark:bg-gray-800 rounded p-2 overflow-x-auto max-h-32 text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all">
                {entry.args}
              </pre>
            </CopyablePre>
          )}
          {entry.result && (
            <CopyablePre value={entry.result} label="result">
              <pre
                className={`text-[10px] leading-tight rounded p-2 overflow-x-auto max-h-32 whitespace-pre-wrap break-all ${
                  entry.isError
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                {entry.result}
              </pre>
            </CopyablePre>
          )}
          {!entry.result && !expandedHasArgs(entry) && (
            <Text size="xs" className="text-muted-foreground">
              No recorded payload for this call.
            </Text>
          )}
        </div>
      )}
    </div>
  );
}

function expandedHasArgs(entry: McpTranscriptEntry): boolean {
  return Boolean(entry.args && entry.args !== '{}');
}
